<template>
  <div class="card">
    <div class="card-header"><h2 style="font-size: 20px;">Notarized Verification of Identity</h2></div>
    <div class="card-body">
      <div>
        <b-row>
          <b-col>
            <p>
              One or more of the products you selected require you to submit a notarized identity verification form. Proof of your
              identity and eligibility is required by law. Please print the identity verification form associated with this order.
              Sign the form in front of a notary public. When you are ready, log back in to the Online Ordering System,
              return to your order, and upload the form. If directed, upload other required documentation, then complete your order.
            </p>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <BaseSelector v-model="notarized"
                          :v="$v.notarized"
                          label="Do you have the identity verification document notarized and prepared for the product(s) you are ordering?"
                          setId="Notarization Dropdown"
                          :options = opts.yesNoBoolOptions
            />
          </b-col>
          <b-col cols="12" md="6" lg="3" offset-sm="0" offset-lg="3" v-if="!notarized">
            <b-button @click="generatePdf()" variant="success" class="mt-2">Download Verification of Identity Form</b-button>
          </b-col>
        </b-row>
        <div v-if="notarized" class="mt-3">
          <b-row>
            <b-col cols="12" md="4" lg="2">
              <FileAttachment
                      v-on:attachment-added="handleUploadSuccess"
                      v-on:attachment-failed="handleUploadFailure"
                      :attachmentTypeOptions="attachmentTypeOptions"
                      :order="orderNumber"
                      describedBy="attachments"
                      textField="description"
                      valueField="code"
                      id="notarize"
              />
            </b-col>
            <b-col cols="12" md="8" lg="10">
              <p>
                Your identity verification form must match the products selected in your order that require verification by law. Please
                confirm that your form has those products listed.
              </p>
            </b-col>
          </b-row>
          <b-row class="mt-5">
            <b-col>
              <AttachmentList
                  :value="attachments"
                  v-on:attachment-removed="handleRemoveSuccess"
                  modalId="notarize"
              />
            </b-col>
          </b-row>
        </div>
      </div>
      <div v-if="requiresNotary === null">
        <b-row>
          <b-col>
            Confirming product notarization requirements, please wait...
          </b-col>
        </b-row>
      </div>
    </div>
    <b-modal size="m" id="register-modal" hide-header hide-footer>
      <h2>
        Do you want to save order details to come back and upload your identity verification form?
      </h2>
      <b-row>
        <b-col cols="12" md="6" align-self="end">
          <b-button variant="primary" @click="login">Log In to your account</b-button>
        </b-col>
        <b-col cols="12" md="6">
          <span class="font-weight-bold">
            Don't have an account?
          </span>
          <b-button variant="success" @click="register" class="mt-3">Register</b-button>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col offset-md="10">
          <b-button @click="hideRegisterModal" variant="secondary">Close</b-button>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>
<script>
import BaseSelector from '@/components/templates/input/BaseSelector.vue';
import FileAttachment from '@/components/templates/input/FileAttachment.vue';
import AttachmentList from '@/components/templates/display/AttachmentList.vue';
import {commonOptions} from "@/mixins/commonOptions";
import {toastService} from "@/mixins/toastService"
import { required, requiredIf } from 'vuelidate/lib/validators'
import axios from "axios"
import axiosRetry from 'axios-retry'
import * as FileSaver from 'file-saver'
import LocalStorageService from "@/common/LocalStorageService";

axiosRetry(axios, { retries: 3,
  retryDelay: axiosRetry.exponentialDelay  })

const localStorageService = LocalStorageService.getService();

export default {
  data: () => {
    return {
      notarized: false,
      attachments: [],
      attachmentTypeOptions: [],
      orderNumber: null,
    }
  },
  props: {
    order: [Object],
    requiresNotary: [Boolean],
  },
  components: {
    BaseSelector,
    AttachmentList,
    FileAttachment
  },
  mixins: [
    commonOptions,
    toastService
  ],
  mounted() {
    this.orderNumber = localStorageService.getOrderNumber()
    this.loadAttachmentTypeOptions()
    this.loadNotaryAttachments()
  },
  validations() {
    return {
      notarized: { required },
      attachments: { required },
      form: [ 'notarized','attachments' ]
    }
  },
  methods: {
    validate() {
      this.$v.form.$touch();
      var isValid = !this.$v.form.$invalid
      this.$emit('on-validate', this.$data, isValid)
      return isValid
    },
    loadAttachmentTypeOptions() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/types/notarize`)
              .then(response => {
                this.attachmentTypeOptions = response.data
              })
    },
    loadNotaryAttachments() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/current/${this.orderNumber}/VERIFY`)
          .then(response => {
            if(response.data) {
              this.attachments = response.data
            }
          })
    },
    generatePdf(){
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/affirm/verify/${this.orderNumber}`)
              .then(response => {
                const file = response.data;
                if (file.contentType == null){
                  return;
                }
                const byteString = atob(file.fileData); //deprecated, update later
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const int8Array = new Uint8Array(arrayBuffer);
                for(let i=0;i<byteString.length;i++){
                  int8Array[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([int8Array], {type: file.contentType});
                FileSaver.saveAs(blob, file.name);
                if (!this.$keycloak.authenticated) {
                  this.showRegisterModal()
                }
              })
    },
    handleUploadSuccess(value) {
      toastService.showRootToast('File Uploaded', 'The file has been successfully uploaded to this application', 'success', this.$root);
      this.attachments.push(value)
    },
    handleUploadFailure(){
      toastService.showRootToast('Upload Failed', 'File Upload Failed','danger', this.$root);
    },
    handleRemoveSuccess(value) {
      let index = this.attachments.findIndex(i => i.attachmentId === value)
      this.attachments.splice(index, 1);
    },
    showRegisterModal(current){
      this.$bvModal.show('register-modal')
    },
    hideRegisterModal(){
      this.$bvModal.hide('register-modal')
    },
    register() {
      this.$router.push({ name: 'register', params: { redirect: 'checkout' } })
    },
    login() {
      this.$keycloak.login()
    }
  }
}
</script>
